import { useState, useEffect } from 'react';

// Get global breakpoints from window (set in head-scripts.liquid)
const getGlobalBreakpoints = () => {
  if (typeof window !== 'undefined' && window.responsiveBreakpoints) {
    return {
      mobile: window.responsiveBreakpoints.mobile,
      tablet: window.responsiveBreakpoints.tablet,
      desktop: window.responsiveBreakpoints.desktop,
      ultrawide: window.responsiveBreakpoints.ultrawide
    };
  }

  // Fallback breakpoints
  return {
    mobile: 767,
    tablet: 1023,
    desktop: 1439,
    ultrawide: 1440
  };
};

function useMediaQuery(min, max) {
  const mediaQuery = window.matchMedia(
    `${min ? `(min-width: ${min}px)` : ''} ${max ? `${min ? 'and' : ''} (max-width: ${max}px)` : ''}`
  );
  const [isMatch, setIsMatch] = useState(mediaQuery.matches);

  useEffect(() => {
    // Function to update state based on the media query result
    function handleBreakpointChange(e) {
      setIsMatch(e.matches);
    }

    // Add event listener
    mediaQuery.addEventListener('change', (e) => setIsMatch(e.matches));

    // Check immediately on mount (important for SSR and initial render)
    handleBreakpointChange(mediaQuery);

    // Clean up
    return () => mediaQuery.removeEventListener('change', handleBreakpointChange);
  }, []);

  return isMatch;
}

// Enhanced hook that provides responsive breakpoint states
export const useResponsiveBreakpoints = () => {
  const breakpoints = getGlobalBreakpoints();

  // Between breakpoints (existing behavior)
  const isMobile = useMediaQuery(null, breakpoints.mobile);
  const isTablet = useMediaQuery(breakpoints.mobile + 1, breakpoints.tablet);
  const isDesktop = useMediaQuery(breakpoints.tablet + 1, breakpoints.desktop);
  const isUltrawide = useMediaQuery(breakpoints.ultrawide, null);

  // Above each breakpoint (new functionality)
  const isAboveMobile = useMediaQuery(breakpoints.mobile + 1, null);
  const isAboveTablet = useMediaQuery(breakpoints.tablet + 1, null);
  const isAboveDesktop = useMediaQuery(breakpoints.desktop + 1, null);
  const isAboveUltrawide = useMediaQuery(breakpoints.ultrawide + 1, null);

  return {
    // Between breakpoints
    isMobile,
    isTablet,
    isDesktop,
    isUltrawide,
    // Above breakpoints
    isAboveMobile,
    isAboveTablet,
    isAboveDesktop,
    isAboveUltrawide,
    breakpoints
  };
};

export default useMediaQuery;
export { getGlobalBreakpoints };
